import {
  fromEvent, Observable, asapScheduler, Subscription,
} from 'rxjs';

import { DOCUMENT } from '@angular/common';
import {
  Component,
  HostListener,
  Inject,
  OnInit,
  Optional,
  ElementRef,
  ViewChild,
  Renderer2,
  TemplateRef,
} from '@angular/core';
import {
  compassIcon,
  heartIcon,
  homeIcon,
  plusIcon,
  profileIcon,
  searchIcon,
  UIKitIconRegistry,
  ModalService,
  CookieConsentService, closeIcon, StorageService, STORAGE, chevronLeftIcon, chevronRightIcon, starIcon, AnalyticsService,
} from '@ui-kit';

import {
  filter, tap, map, startWith,
} from 'rxjs/operators';
import {
  Router, NavigationEnd, ActivatedRoute, NavigationStart,
} from '@angular/router';
import { pick, findIndex } from 'lodash-es';
// eslint-disable-next-line import/no-unresolved
import { P } from '@ui-kit/ui-kit';
import { AppService, AppWebView, AppEvent } from './app.service';
import { UserService } from './shared/service/user.service';
import { NotificationService } from './shared/service';
import { trackById, clearSubscription } from './app.helpers';
import { AppNotification } from './shared/component/notification/notification.helpers';
import { LeanFavourite } from './public/favourites/shortlist.helpers';
import { environment } from '../environments/environment';

// change this date if you need the budget notification modal to show up again
const CALCLOAN_RELEASE = new Date('May 16 2022');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  static DISMISS_INSTALL_KEY = 'current_user_dismiss_install';

  static BOOK_A_CALL_MODAL_ID = `${AppComponent.name}-book-call-modal`;

  static BUDGET_NOTIFICATION_MODAL_ID = 'budget-notification-modal';

  static BUDGET_NOTIFICATION_STORAGE_KEY = `budget-notification-key-${CALCLOAN_RELEASE.getTime()}`;

  @ViewChild('addHomeTemplate') addHomeTemplate: TemplateRef<unknown>;

  @ViewChild('bookCallTemplate') bookCallTemplate: TemplateRef<unknown>;

  @ViewChild('offerAcceptedTemplate') offerAcceptedTemplate: TemplateRef<unknown>;

  offerAcceptedSelected: LeanFavourite;

  @ViewChild('webViewTemplate') webViewTemplate: TemplateRef<HTMLElement>;

  @ViewChild('main', { static: true }) main: ElementRef<HTMLElement>;

  @ViewChild('budgetNotificationModalTemplate') budgetNotificationModalTemplate: TemplateRef<unknown>;

  signedIn: Observable<boolean> = this.user.signedInObservable
    .pipe(
      startWith(this.user.signedIn),
      tap((signedIn) => this.toggle.signedIn = signedIn),
    );

  favouritesCount: number;

  name: string;

  promptInstallPrompt: boolean;

  toggle: Dictionary<boolean> = {};

  subscription: Dictionary<Subscription> = {};

  notifications: AppNotification[];

  webView: AppWebView;

  trackById = trackById;

  private deferredPrompt; // BeforeInstallPromptEvent

  private removeTags: HTMLElement[] = [];

  @HostListener('window:resize', ['$event'])
  onResize(event?: Event) {
    this.delayedSetVH(300);
  }

  @HostListener('document:visibilitychange', ['$event'])
  onVisibilityChange(event?: Event) {
    this.toggle.visible = this.document.visibilityState === 'visible';
    this.delayedSetVH(300);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event) {

  }

  // @HostListener('scroll', ['$event']) // for scroll events of the current element
  @HostListener('window:scroll', ['$event']) // for window scroll events
  onScroll(event: Event) {
    // this.setVH();
    // this.document.body.scrollTop = this.document.documentElement.scrollTop = 0;
  }

  profileComplete: number;

  currentUser: P.User<P.Applicant>;

  organization: string;

  room: string;

  constructor(
    @Optional() private window: Window,
    @Optional() @Inject(DOCUMENT) private document: Document,
    private app: AppService,
    private user: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
    private modal: ModalService,
    private notification: NotificationService,
    private storage: StorageService,
    private analytics: AnalyticsService,
    private cookieConsent: CookieConsentService,
    iconRegistry: UIKitIconRegistry,
  ) {
    iconRegistry.registerIcons([
      homeIcon,
      searchIcon,
      heartIcon,
      plusIcon,
      compassIcon,
      profileIcon,
      closeIcon,
      chevronLeftIcon,
      chevronRightIcon,
      starIcon,
    ]);
  }

  /**
   *
   */
  ngOnInit() {
    // origin tracking via queryParams
    this.route.queryParams
      .pipe(startWith(this.route.snapshot.queryParams))
      .subscribe((queryParams) => this.user.origin = queryParams);

    // this.router.events
    //   .pipe(filter(event => event instanceof NavigationEnd))
    //   .subscribe((event: NavigationEnd) => this.toggle.profileAndOrAuth = /^\/(signin|signup|reset|forgot_password)$/.test(event.url))

    this.user.event
      .pipe(
        filter((e) => e.type === 'change'),
        map((e) => e.data),
      )
      .subscribe(() => {
        this.currentUser = this.user?.current;

        this.profileComplete = this.user?.profileComplete?.total;

        this.name = (this.user?.current?.application?.applicants || [])
          .filter((applicant) => !!applicant?.first_name)
          .map((applicant) => applicant?.first_name)
          .join(' & ');
      });
    //
    this.app.event
      .pipe(filter((event) => event?.type === 'book-a-call'))
      .subscribe((event) => this.bookCall(event?.data?.organization, event?.data?.room));

    this.app.event
      .pipe(filter((event) => event?.type === 'close-book-a-call'))
      .subscribe((event) => this.modal.close(AppComponent.BOOK_A_CALL_MODAL_ID));

    // pwa install
    this.toggle.standalone = this.app.standalone;
    if (!this.app.standalone) {
      fromEvent((this.window || window), 'beforeinstallprompt')
        .subscribe(this.onBeforeInstallPrompt.bind(this));
      (this.window || window).addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt.bind(this));
    }

    // // force sign out
    // this.user.event
    //   .pipe(filter(event => event.type === 'forceSignOut'))
    //   .subscribe(event => this.modal
    //     .open('Your session has expired. Please signin.')
    //     .finally(() => this.router.navigate(['/'])));

    // webview
    this.app.event
      .pipe(
        filter((event) => event.type === 'trigger-web-view'),
        map((event) => event.data),
        tap((webView) => this.webView = webView),
      )
      .subscribe(() => this.modal
        .open(this.webViewTemplate, { nav: false, maxWidth: 'l' }));

    // add a home
    this.app.event
      .pipe(filter((event) => event.type === 'add-a-home'))
      .subscribe(this.addHome.bind(this));

    // added a home
    this.app.event
      .pipe(filter((event) => event.type === 'dismiss-add-a-home'))
      .subscribe(this.dissmissAddedHome.bind(this));

    // offer accepted
    this.app.event
      .pipe(filter((event) => event.type === 'offer-accepted'))
      .subscribe(this.offerAccepted.bind(this));

    // scroll to
    this.app.event
      .pipe(filter((event) => event.type === 'scroll-to'))
      .subscribe((event) => this.renderer.setProperty(this.main.nativeElement, 'scrollTop', event.data || '0'));

    // notifications
    this.notification.event
      .pipe(
        filter((event) => event.type === 'new'),
        map((event) => event.data),
      )
      .subscribe(this.notify.bind(this));

    // navigation end events
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
      )
      .subscribe((event: NavigationStart) => asapScheduler.schedule(() => this.toggle.hideExtras = /\/\homes/.test(event.url), 0));

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap(() => this.delayedSetVH(300)),
        // tap(() => this.app.windowObject('zE', 'hide', zendesk => zendesk('webWidget', 'hide'))),
        tap(() => {
          while (this.removeTags.length > 0) {
            const tag = this.removeTags.shift();
            this.renderer.removeChild(this.document.body, tag);
          }
        }),
        tap(() => this.notifications = []),
      )
      .subscribe(this.onResize.bind(this));

    this.app.event
      .pipe(filter((event) => event.type === 'trigger-rightmove' && environment.production))
      .subscribe((event) => {
        let xsp: string;
        switch (event.data) {
          case 'enquiry':
            xsp = '4827181';
            break;
          case 'booked-a-call':
            xsp = '4797206';
            break;
          case 'signup':
          default:
            xsp = '4797203';
            break;
        }
        //
        const noScript: HTMLElement = this.renderer.createElement('noscript');
        const a = `${Math.random() * 10000000000000}`;
        const img: HTMLImageElement = this.renderer.createElement('img');
        img.width = 0;
        img.height = 0;
        img.src = `https://pubads.g.doubleclick.net/activity;xsp=${xsp};ord=${a}?`;
        this.renderer.appendChild(noScript, img);
        this.renderer.appendChild(this.document.body, noScript);

        this.removeTags.push(noScript);
      });

    this.app.event
      .pipe(filter((event) => event.type === 'trigger-pinterest' && environment.production))
      .subscribe((event) => {
        if (typeof (this.window as any)?.pintrk === 'function') {
          const script: HTMLScriptElement = this.renderer.createElement('script');
          const inlineScript = this.renderer.createText(`pintrk('track', '${event.data}')`);
          this.renderer.appendChild(script, inlineScript);
          this.renderer.appendChild(this.document.body, script);
          //
          const noScript: HTMLElement = this.renderer.createElement('noscript');
          const img: HTMLImageElement = this.renderer.createElement('img');
          img.width = 1;
          img.height = 1;
          this.renderer.setStyle(img, 'display', 'none');
          img.src = `https://ct.pinterest.com/v3/?tid=2614481749724&event=${event.data}&noscript=1`;
          this.renderer.appendChild(noScript, img);
          this.renderer.appendChild(this.document.body, noScript);
          // (this.window as any)?.pintrk('track', event.data);

          this.removeTags.push(script, noScript);
        }
      });

    this.app.event
      .pipe(filter((event) => event.type === 'working'))
      .subscribe((event) => {
        // set to server timeout
        const timeOut = 10000;
        // set kind of working indicator
        this.working = event.data.working
          ? event.data.uiBlocking
            ? 'ui-blocking'
            : 'non-ui-blocking'
          : void 0;
        // reset timeout
        if (this.workingTimeout) {
          this.workingTimeout.unsubscribe();
        }
        // if working, set timeout
        if (this.working) {
          this.workingTimeout = asapScheduler
            .schedule(() => this.working = void 0, timeOut);
        }
      });

    this.app.event
      .pipe(filter((event) => event.type === 'trigger-resize'))
      .subscribe(this.onResize.bind(this));

    this.modal.event
      .subscribe(this.onResize.bind(this));

    this.onResize();
  }

  working: 'ui-blocking' | 'non-ui-blocking' | undefined;

  workingTimeout: Subscription | undefined;

  ngAfterViewInit() {
    if (this.needToShowBudgetNotification) {
      this.modal.open(this.budgetNotificationModalTemplate, {
        id: AppComponent.BUDGET_NOTIFICATION_MODAL_ID,
        nav: false,
        maxWidth: 'm',
      });
    }
  }

  /**
   *
   */
  addHome(event: AppEvent | MouseEvent | TouchEvent): void {
    this.modal
      .open(this.addHomeTemplate, { id: `${AppComponent.name}-add-home`, nav: false, maxWidth: 'm' });
  }

  /**
   *
   */
  offerAccepted(event: AppEvent | MouseEvent | TouchEvent): void {
    const selected = (event as AppEvent)?.data;
    this.offerAcceptedSelected = selected;

    const modalId = `${AppComponent.name}-offer-accepted-modal`;
    this.modal
      .open(this.offerAcceptedTemplate, { id: modalId, nav: false, maxWidth: 'm' })
      .finally(() => {
        clearSubscription(modalId, this.subscription);
        this.offerAcceptedSelected = void 0;
      });

    this.subscription[modalId] = this.app.event
      .pipe(filter((appEvent) => appEvent.type === 'offer-accepted-confirm'))
      .subscribe(() => this.router.navigate(['/'])
        .then(() => this.modal.close(modalId)));
  }

  private get needToShowBudgetNotification() {
    const isDismissed = this.storage.get(STORAGE.LOCAL, AppComponent.BUDGET_NOTIFICATION_STORAGE_KEY);
    const isOldUser = new Date(this.user.current?.createdAt) < CALCLOAN_RELEASE;
    return !isDismissed
      && this.signedIn
      && this.user.qualifiesForBookACall
      && isOldUser;
  }

  onBudgetNotificationDismissClick() {
    this.closeBudgetNotification();
    this.storage.set(STORAGE.LOCAL, AppComponent.BUDGET_NOTIFICATION_STORAGE_KEY, '1');
  }

  onBudgetNotificationCTAClick() {
    this.onBudgetNotificationDismissClick();
    this.bookCall();
  }

  closeBudgetNotification() {
    this.modal.close(AppComponent.BUDGET_NOTIFICATION_MODAL_ID);
  }

  bookCall(organization: string = 'proportunity', room: string = 'chat-with-us'): void {
    this.organization = organization;
    this.room = room;
    this.modal.close(AppComponent.BOOK_A_CALL_MODAL_ID);
    this.modal
      .open(this.bookCallTemplate, {
        id: AppComponent.BOOK_A_CALL_MODAL_ID,
        nav: false,
      });
  }

  bookCallEvent(event): void {
    this.analytics.eventTrack({
      data: {
        event: 'http',
        action: event?.type,
        label: 'app',
        properties: {
          value: '1',
          category: 'app',
          route: this.router.url,
        },
      },
    });
    //
    if (event?.type === 'booked-a-call') {
      this.app.event.emit(event);
      this.app.event.emit({ type: 'trigger-rightmove', data: 'booked-a-call' });

      const email = event?.data?.call?.email;
      if (!this.user.signedIn
        && !this.user.current.email
        && !!email) {
        this.user
          .update({ email })
          .toPromise();
      }
    }
    //
    if (event?.type === 'close-booked-a-call') {
      this.modal.close(AppComponent.BOOK_A_CALL_MODAL_ID);
      this.app.event.emit({ type: 'close-book-a-call' });
    }
  }

  /**
   *
   */
  promptInstall(event: MouseEvent | TouchEvent): void {
    //
    this.promptInstallPrompt = false;
    //
    if (this.deferredPrompt) {
      // Show the install prompt
      this.deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      this.deferredPrompt?.userChoice
        .then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
            fromEvent(window, 'appinstalled')
              .subscribe(this.onAppInstalled.bind(this));
          } else {
            this.promptInstallPrompt = true;
          }
        });
    } else {
      this.modal
        .open('An unespected problem occurred. Please try again later.')
        .finally(this.dismissInstall.bind(this));
    }
  }

  /**
   *
   */
  dismissInstall(event?: MouseEvent | TouchEvent): void {
    //
    this.promptInstallPrompt = false;
    //
    this.storage.set(STORAGE.COOKIE, AppComponent.DISMISS_INSTALL_KEY, '1', {
      expires: 30,
      secure: environment.production,
      path: '/',
      domain: environment.environment === 'local'
        ? '.proportunity.local'
        : '.proportunity.com',
    });
  }

  /**
   *
   */
  onNotificationDismiss(notification: AppNotification): void {
    const index = findIndex(this.notifications, { id: notification.id });
    if (index > -1) {
      this.notifications.splice(index, 1);
    }
  }

  backToWww(event: MouseEvent): void {
    this.window.open(environment.urls.www, '_blank');
  }

  /**
   *
   */
  private dissmissAddedHome(): void {
    this.modal.close(`${AppComponent.name}-add-home`);
  }

  /**
   *
   */
  private onBeforeInstallPrompt(event: Event): void {
    // Prevent the mini-info bar from appearing on mobile
    event.preventDefault();
    // Stash the event so it can be triggered later.
    this.deferredPrompt = event;
    //
    const dismissedInstall = !!this.storage.get(STORAGE.COOKIE, AppComponent.DISMISS_INSTALL_KEY);
    //
    this.promptInstallPrompt = !dismissedInstall; // not previously dismissed
    // && this.user.signedIn; // user signed in
  }

  /**
   *
   */
  private onAppInstalled(event: Event): void {
    this.modal.open('App installed successfully!');
  }

  /**
   *
   */
  private notify(notifications: AppNotification[]): void {
    this.notifications = notifications;

    if (this.document.visibilityState !== 'visible'
      && this.notification.nativeNotificationPermission) {
      for (const notification of notifications) {
        const nativeNotification = new Notification(notification.badge || 'Proportunity', pick(notification, ['body', 'icon']));
      }
    }
  }

  /**
   * set --vh global variable
   */
  private setVH(target?: HTMLElement): void {
    const vh = (this.window || window).innerHeight;
    const vhPx = `${vh}px`;
    //
    this.document.documentElement.style.setProperty('--vh', vhPx);
    this.renderer.setProperty((this.document || document).documentElement, '--vh', vhPx);
  }

  private delayedSetVH(delay: number): void {
    const subscriptionKey = `${AppComponent.name}-set-vh`;
    clearSubscription(subscriptionKey, this.subscription);
    this.subscription[subscriptionKey] = asapScheduler
      .schedule(this.setVH.bind(this), delay);
  }
}
